import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Caption1, Checkbox, Field, Input, MessageBar, MessageBarBody, Select, Textarea, makeStyles } from '@fluentui/react-components';
import {
  LANGUAGE_CHOICES, MAX_OTHER_LANGUAGE_LENGTH, MAX_STATUS_NOTE_LENGTH, effectiveOnSchema, hrWorkflowCommandSchema, reasonChoicesFor, reasonQuestions,
  personDisplayName, returnsWithoutActivationCheck, staffMovesFrom, statusReasonLabels,
  type LanguageId, type LifecycleState, type Person, type ReasonState, type ServicePlace, type StatusReason, type UpdatePerson,
} from '@vigeo/shared';
import { PopupDialog, shellMedia, tokens } from '@vigeo/ui';
import { useNavigate } from 'react-router-dom';
import { lifecyclePresenters } from '@/features/candidates';
import { ROSTER_PATH } from '@/lib/sections';
import { PlacePicker } from '@/features/places';
import { performHrWorkflow } from '@/features/hr-workflow';
import { changeLifecycle, fetchCatalogOptions, updatePerson } from '@/lib/api';
import { useEditGuard } from './EditGuard.js';
import { Card } from './parts.js';
import { groupFields, groupIsDirty, groupPatch, groupTitles, groupValues, languagesDraftOf, problemsOf, type FieldGroup, type LanguagesDraft, type SeparatedNotice } from './profile.js';

const useStyles = makeStyles({
  form: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  grid: {
    display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: tokens.spacingHorizontalM,
    [shellMedia.phone]: { gridTemplateColumns: 'minmax(0, 1fr)' },
  },
  whole: { gridColumnStart: 1, gridColumnEnd: -1 },
  roles: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalS, border: 'none', padding: 0, margin: 0 },
  legend: { padding: 0, fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300, fontWeight: tokens.fontWeightSemibold },
  foot: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  footNote: { flexGrow: 1, minWidth: 0, color: tokens.colorNeutralForeground2 },
  editing: { color: tokens.colorBrandForeground1, fontWeight: tokens.fontWeightSemibold },
});

/** What every profile save refreshes: the record, the lists that show it, and the history that just gained a row. */
export function useRefreshPerson(personId: string) {
  const client = useQueryClient();
  return () => Promise.all(
    [['person', personId], ['people'], ['checklist', personId], ['dashboard'], ['audit', personId], ['timeline', personId], ['employee-profile', 'timeline', personId], ['controlled-forms', personId]]
      .map((queryKey) => client.invalidateQueries({ queryKey })),
  );
}

export interface GroupCardProps {
  /** The guard's name for this group; one group is open at a time. */
  readonly id: string;
  readonly title: string;
  /** Someone who may change the record. A viewer gets no Edit button; the server refuses the write whatever the page draws. */
  readonly canEdit: boolean;
  /** The button's words when the group is empty: "Add emergency contact". */
  readonly editLabel?: string;
  /** Shown when not editing. */
  readonly children: ReactNode;
  /** The form, drawn in place of `children` while this group is the one being edited. */
  readonly form: () => ReactNode;
}

/** One group of the profile: its facts, and for someone who may edit, an Edit that turns the card into a form in the page (Figma `1096:804`). */
export function GroupCard({ id, title, canEdit, editLabel = 'Edit', children, form }: GroupCardProps) {
  const styles = useStyles();
  const guard = useEditGuard();
  const editing = canEdit && guard.editing === id;
  const button = useRef<HTMLButtonElement>(null);
  // When the form closes, the keyboard goes back to the button that opened it. `wasEditing` keeps the first draw from stealing it.
  const wasEditing = useRef(false);
  useEffect(() => {
    if (editing) { wasEditing.current = true; return; }
    if (wasEditing.current) { wasEditing.current = false; button.current?.focus(); }
  }, [editing]);
  return (
    <Card
      title={title}
      editing={editing}
      aside={editing ? <Caption1 className={styles.editing}>Editing</Caption1> : canEdit ? (
        <Button ref={button} appearance="secondary" aria-label={editLabel === 'Edit' ? `Edit ${title}` : undefined} onClick={() => guard.begin(id)}>{editLabel}</Button>
      ) : undefined}
    >
      {editing ? form() : children}
    </Card>
  );
}

/** Cancel on the left, the one primary on the right, and the sentence that says the change is recorded. */
function FormFoot({ busy, onCancel, saveLabel = 'Save' }: { readonly busy: boolean; readonly onCancel: () => void; readonly saveLabel?: string }) {
  const styles = useStyles();
  return (
    <div className={styles.foot}>
      <Caption1 className={styles.footNote}>Saving records who changed what, and when, in History.</Caption1>
      <Button appearance="secondary" disabled={busy} onClick={onCancel}>Cancel</Button>
      <Button appearance="primary" type="submit" disabled={busy}>{busy ? 'Saving…' : saveLabel}</Button>
    </div>
  );
}

/**
 * One save at a time, and the guard told while it is on its way (review of #586, findings 4 and 5).
 *
 * `start` answers whether this submit may send. It reads a ref, not `isPending`: three submits in one tick all saw the same
 * render, in which nothing was pending yet, and each sent its own request and wrote its own history row.
 */
function useOneSave(pending: boolean): { readonly start: () => boolean; readonly stop: () => void } {
  const { setBusy } = useEditGuard();
  const sending = useRef(false);
  useEffect(() => { setBusy(pending); }, [pending, setBusy]);
  return {
    start: () => { if (sending.current) return false; sending.current = true; return true; },
    stop: () => { sending.current = false; },
  };
}

const HINTS: Partial<Record<FieldGroup, string>> = {
  emergency: 'For each emergency contact, enter a name, relationship and phone, or leave the whole contact blank.',
  emr: 'Record the account identifier and setup status. Do not enter passwords or access codes.',
};

/** The form for a group that is a list of fields. Employment also holds the roles, as tick boxes: a person may hold more than one. */
export function FieldGroupForm({ person, group }: { readonly person: Person; readonly group: FieldGroup }) {
  const styles = useStyles();
  const guard = useEditGuard();
  const refresh = useRefreshPerson(person.id);
  const fields = groupFields[group];
  const [draft, setDraft] = useState<Record<string, string>>(() => groupValues(person, group));
  const [roles, setRoles] = useState<readonly string[]>(person.jobProfileKeys);
  const [languages, setLanguages] = useState<LanguagesDraft>(() => languagesDraftOf(person));
  const [problem, setProblem] = useState<string | null>(null);
  const withRoles = group === 'employment';
  // Languages spoken sit in the group that holds the personal details (#772).
  const withLanguages = group === 'identity';
  const options = useQuery({ queryKey: ['catalog-options'], queryFn: fetchCatalogOptions, enabled: withRoles });
  const save = useMutation({
    mutationFn: (patch: UpdatePerson) => updatePerson(person.id, patch),
    onSuccess: async () => { await refresh(); guard.end(); },
    onSettled: () => one.stop(),
  });
  const one = useOneSave(save.isPending);

  const dirty = groupIsDirty(person, group, draft, withRoles ? roles : undefined, withLanguages ? languages : undefined);
  const { setDirty } = guard;
  useEffect(() => { setDirty(dirty); }, [dirty, setDirty]);

  function submit(event: FormEvent) {
    event.preventDefault();
    if (save.isPending) return;
    try {
      const patch = groupPatch(person, group, draft, withRoles ? roles : undefined, withLanguages ? languages : undefined);
      setProblem(null);
      if (Object.keys(patch).length === 0) { guard.end(); return; }
      if (one.start()) save.mutate(patch);
    } catch (error) {
      setProblem(problemsOf(error, fields));
    }
  }

  const known = options.data?.jobProfiles ?? [];
  const retired = person.jobProfileKeys.filter((key) => !known.some((profile) => profile.key === key));
  const toggle = (key: string, on: boolean) => setRoles(on ? [...new Set([...roles, key])] : roles.filter((role) => role !== key));
  const tick = (id: LanguageId, on: boolean) => setLanguages((current) => ({ ...current, spoken: on ? [...new Set([...current.spoken, id])] : current.spoken.filter((spoken) => spoken !== id) }));
  return (
    <form className={styles.form} aria-label={`Edit ${groupTitles[group]}`} onSubmit={submit} noValidate>
      {problem !== null || save.isError ? <MessageBar intent="error"><MessageBarBody>{problem ?? save.error?.message}</MessageBarBody></MessageBar> : null}
      {HINTS[group] === undefined ? null : <Caption1>{HINTS[group]}</Caption1>}
      {withRoles ? (
        <fieldset className={styles.roles}>
          <legend className={styles.legend}>Role</legend>
          {options.isPending ? <Caption1>Loading the roles…</Caption1> : options.isError ? <Caption1>The list of roles could not be loaded. The other fields can still be saved.</Caption1> : null}
          {known.map((profile) => <Checkbox key={profile.key} label={profile.label} checked={roles.includes(profile.key)} disabled={save.isPending} onChange={(_, data) => toggle(profile.key, data.checked === true)} />)}
          {retired.map((key) => <Checkbox key={key} label="Role no longer offered" checked={roles.includes(key)} disabled={save.isPending} onChange={(_, data) => toggle(key, data.checked === true)} />)}
        </fieldset>
      ) : null}
      <div className={styles.grid}>
        {fields.map((field) => {
          const value = draft[field.path] ?? '';
          const change = (next: string) => setDraft((current) => ({ ...current, [field.path]: next }));
          return (
            <Field key={field.path} label={field.label} className={field.multiline ? styles.whole : undefined}>
              {field.choices ? (
                <Select value={value} disabled={save.isPending} onChange={(_, data) => change(data.value)}>
                  {field.choices.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                </Select>
              ) : field.multiline ? <Textarea value={value} disabled={save.isPending} onChange={(_, data) => change(data.value)} />
                : <Input type={field.type ?? 'text'} value={value} disabled={save.isPending} onChange={(_, data) => change(data.value)} />}
            </Field>
          );
        })}
      </div>
      {withLanguages ? (
        <fieldset className={styles.roles}>
          <legend className={styles.legend}>Languages spoken</legend>
          {LANGUAGE_CHOICES.map((choice) => <Checkbox key={choice.id} label={choice.label} checked={languages.spoken.includes(choice.id)} disabled={save.isPending} onChange={(_, data) => tick(choice.id, data.checked === true)} />)}
          {languages.spoken.includes('other') ? (
            <Field label="Which other language?" className={styles.whole}>
              <Input value={languages.other} maxLength={MAX_OTHER_LANGUAGE_LENGTH} disabled={save.isPending} onChange={(_, data) => setLanguages((current) => ({ ...current, other: data.value }))} />
            </Field>
          ) : null}
        </fieldset>
      ) : null}
      <FormFoot busy={save.isPending} onCancel={() => guard.request(() => undefined)} />
    </form>
  );
}

const sameIds = (a: readonly ServicePlace[], b: readonly ServicePlace[]) => a.length === b.length && a.every((place, i) => place.placeId === b[i]?.placeId);

/** Service areas, picked from the same search box the Roster's form uses (ADR-018). Only the places are sent; the older typed text is never changed here. */
export function ServiceAreaForm({ person }: { readonly person: Person }) {
  const styles = useStyles();
  const guard = useEditGuard();
  const refresh = useRefreshPerson(person.id);
  const initial = person.assignment?.servicePlaces ?? [];
  const [places, setPlaces] = useState<readonly ServicePlace[]>(initial);
  const typedArea = person.assignment?.serviceArea?.trim() ?? '';
  const save = useMutation({
    mutationFn: () => updatePerson(person.id, { assignment: { servicePlaces: [...places] } }),
    onSuccess: async () => { await refresh(); guard.end(); },
    onSettled: () => one.stop(),
  });
  const one = useOneSave(save.isPending);
  const dirty = !sameIds(places, initial);
  const { setDirty } = guard;
  useEffect(() => { setDirty(dirty); }, [dirty, setDirty]);
  return (
    <form
      className={styles.form}
      aria-label={`Edit ${groupTitles['service-area']}`}
      onSubmit={(event) => { event.preventDefault(); if (save.isPending) return; if (!dirty) { guard.end(); return; } if (one.start()) save.mutate(); }}
    >
      {save.isError ? <MessageBar intent="error"><MessageBarBody>{save.error?.message}</MessageBarBody></MessageBar> : null}
      <PlacePicker value={places} onChange={setPlaces} />
      {places.length === 0 && typedArea !== '' ? (
        <Caption1>Service area typed before places existed: “{typedArea}”. It stays on the record. Pick places above and they show instead.</Caption1>
      ) : null}
      <FormFoot busy={save.isPending} onCancel={() => guard.request(() => undefined)} />
    </form>
  );
}

/** A staff note, through the workflow's existing `comment` command. */
export function NoteForm({ person }: { readonly person: Person }) {
  const styles = useStyles();
  const guard = useEditGuard();
  const client = useQueryClient();
  const refresh = useRefreshPerson(person.id);
  const [text, setText] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [operationId] = useState(() => crypto.randomUUID());
  const save = useMutation({
    mutationFn: () => performHrWorkflow(person.id, hrWorkflowCommandSchema.parse({ action: 'comment', operationId, text })),
    onSuccess: async (view) => { client.setQueryData(['hr-workflow', person.id], view); await refresh(); guard.end(); },
    onSettled: () => one.stop(),
  });
  const one = useOneSave(save.isPending);
  const dirty = text.trim() !== '';
  const { setDirty } = guard;
  useEffect(() => { setDirty(dirty); }, [dirty, setDirty]);
  return (
    <form
      className={styles.form}
      aria-label="Add a note"
      onSubmit={(event) => {
        event.preventDefault();
        if (save.isPending) return;
        if (!dirty) { setProblem('Type the note first.'); return; }
        setProblem(null);
        if (one.start()) save.mutate();
      }}
    >
      {problem !== null || save.isError ? <MessageBar intent="error"><MessageBarBody>{problem ?? save.error?.message}</MessageBarBody></MessageBar> : null}
      <Field label="Note" hint="Do not type health details or a Social Security number here.">
        <Textarea value={text} disabled={save.isPending} maxLength={4000} onChange={(_, data) => setText(data.value)} />
      </Field>
      <FormFoot busy={save.isPending} onCancel={() => guard.request(() => undefined)} saveLabel="Save note" />
    </form>
  );
}

/** The words for each staff move on the status form (#810): where the person goes, said the way HR says it. */
const MOVE_WORDS: Readonly<Record<'active' | 'inactive' | 'separated', string>> = {
  inactive: 'Inactive: on leave, suspended or other',
  active: 'Active: back at work',
  separated: 'Separated: no longer works here',
};
const moveWords = (to: LifecycleState): string => (to === 'active' || to === 'inactive' || to === 'separated' ? MOVE_WORDS[to] : lifecyclePresenters[to].label);

/**
 * A staff member's status change: on leave, back from leave, leaving (ADR-016 Amendment 2, #810).
 *
 * Only the staff moves are offered (`staffMovesFrom`), the same list the Roster-only server makes; every candidate and hiring
 * move is on the hiring record and nowhere here. Inactive and Separated ask for #772's closed reason, with its optional note and
 * the optional day it took effect. The audit row's free-text reason is the picked label alone, as on the hiring record, since the
 * note can hold personal detail and is kept on the person's record only.
 *
 * Coming back to Active asks for nothing for a person added by hand (decision C). A tracked person keeps the activation check;
 * the server derives it, so the form only asks HR to confirm final clearance and shows what the server answers.
 *
 * Separated asks once more before it is sent (decision 3): the person leaves the Roster list, and only Hiring can rehire them.
 */
export function StatusForm({ person }: { readonly person: Person }) {
  const styles = useStyles();
  const guard = useEditGuard();
  const refresh = useRefreshPerson(person.id);
  const navigate = useNavigate();
  const moves = staffMovesFrom(person.lifecycle);
  const [to, setTo] = useState<LifecycleState | ''>(moves.length === 1 ? moves[0]! : '');
  const [statusReason, setStatusReason] = useState<StatusReason | ''>('');
  const [statusNote, setStatusNote] = useState('');
  const [effectiveOn, setEffectiveOn] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [asking, setAsking] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const choices = to === '' ? [] : reasonChoicesFor(to);
  const clearance = to === 'active' && !returnsWithoutActivationCheck(person, 'active');
  const typedDate = effectiveOn.trim();
  const reasonText = choices.length > 0 ? (statusReason === '' ? '' : statusReasonLabels[statusReason]) : 'Back from leave';

  const save = useMutation({
    mutationFn: () => changeLifecycle(person.id, {
      to: to as LifecycleState,
      reason: reasonText,
      ...(clearance ? { confirmFinalHrClearance: confirmed } : {}),
      ...(choices.length > 0 && statusReason !== '' ? { statusReason, ...(statusNote.trim() === '' ? {} : { statusNote: statusNote.trim() }) } : {}),
      ...(typedDate === '' ? {} : { effectiveOn: typedDate }),
    }),
    onSuccess: async () => {
      setAsking(false);
      if (to === 'separated') {
        // A separated person is not on the Roster, and their profile is "no such person" from now on (decision 3). So HR goes
        // back to the list, which says who was just marked, instead of staying on a page that can no longer be read (#810).
        guard.end();
        const notice: SeparatedNotice = { separated: personDisplayName(person.name) };
        void navigate(ROSTER_PATH, { state: notice });
        await refresh();
        return;
      }
      await refresh();
      guard.end();
    },
    onError: () => setAsking(false),
    onSettled: () => one.stop(),
  });
  const one = useOneSave(save.isPending);

  const dirty = (moves.length > 1 && to !== '') || statusReason !== '' || statusNote !== '' || effectiveOn !== '' || confirmed;
  const { setDirty } = guard;
  useEffect(() => { setDirty(dirty); }, [dirty, setDirty]);

  /** What stops the form from being sent, in the order HR fills it in; null when it can go. */
  function blocker(): string | null {
    if (to === '') return 'Choose the new status.';
    if (choices.length > 0 && statusReason === '') return `Choose a reason: ${reasonQuestions[to as ReasonState]}`;
    if (typedDate !== '' && !effectiveOnSchema.safeParse(typedDate).success) return 'Enter a real date it took effect, or leave it blank.';
    if (clearance && !confirmed) return 'Confirm final HR clearance first.';
    return null;
  }

  function send() { if (one.start()) save.mutate(); }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (save.isPending) return;
    const stop = blocker();
    setProblem(stop);
    if (stop !== null) return;
    if (to === 'separated') { setAsking(true); return; }
    send();
  }

  const display = person.name.preferredName || person.name.givenName;
  return (
    <form className={styles.form} aria-label="Change status" onSubmit={submit} noValidate>
      {problem !== null || save.isError ? <MessageBar intent="error"><MessageBarBody>{problem ?? save.error?.message}</MessageBarBody></MessageBar> : null}
      <Field label="New status" required>
        <Select value={to} disabled={save.isPending} onChange={(_, data) => { setTo(data.value as LifecycleState | ''); setStatusReason(''); setConfirmed(false); }}>
          {moves.length > 1 ? <option value="">Choose the new status</option> : null}
          {moves.map((move) => <option key={move} value={move}>{moveWords(move)}</option>)}
        </Select>
      </Field>
      {choices.length > 0 ? <>
        <Field label={reasonQuestions[to as ReasonState]} required>
          <Select value={statusReason} disabled={save.isPending} onChange={(_, data) => setStatusReason(data.value as StatusReason | '')}>
            <option value="">Choose a reason</option>
            {choices.map((choice) => <option key={choice.id} value={choice.id}>{choice.label}</option>)}
          </Select>
        </Field>
        <Field label="Note (optional)" hint="Kept on this person's record. It is not copied to the audit trail. Do not type health details here.">
          <Input value={statusNote} maxLength={MAX_STATUS_NOTE_LENGTH} disabled={save.isPending} onChange={(_, data) => setStatusNote(data.value)} />
        </Field>
      </> : null}
      {to === '' ? null : (
        <Field label="Date it took effect (optional)" hint="Leave blank if it took effect when you record it. The time you record it is kept either way.">
          <Input type="date" value={effectiveOn} disabled={save.isPending} onChange={(_, data) => setEffectiveOn(data.value)} />
        </Field>
      )}
      {clearance ? (
        <Checkbox checked={confirmed} disabled={save.isPending} label="I confirm final HR clearance and that the employee may begin work." onChange={(_, data) => setConfirmed(data.checked === true)} />
      ) : null}
      <FormFoot busy={save.isPending} onCancel={() => guard.request(() => undefined)} saveLabel="Change status" />
      <PopupDialog
        open={asking}
        title={`Mark ${display} as Separated?`}
        size="small"
        startOn="cancel"
        cancelLabel="Go back"
        busy={save.isPending}
        primary={{ label: 'Mark as Separated', busyLabel: 'Saving…', onClick: send }}
        onClose={() => setAsking(false)}
      >
        They leave the Roster list, and the only way back is to rehire them through Hiring. Their record and history are kept.
      </PopupDialog>
    </form>
  );
}
